import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Facebook, Instagram, Linkedin, Twitter, Unplug, Link2, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialPlatforms = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', name: 'X/Twitter', icon: Twitter, color: '#000000' },
];

export default function SocialIntegrations() {
  const [accounts, setAccounts] = useState([]);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [connectUsername, setConnectUsername] = useState('');
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/oauth/connections`);
      const providerToPlatform = { meta: 'facebook', linkedin_ads: 'linkedin' };
      const mapped = (data.connections || [])
        .filter(c => c.connected && providerToPlatform[c.provider])
        .map(c => ({
          id: `${c.provider}:${c.account_id || c.tenant_id}`,
          platform: providerToPlatform[c.provider],
          username: c.account_name || c.account_id,
          provider: c.provider,
        }));
      const metaConn = (data.connections || []).find(c => c.provider === 'meta' && c.connected);
      if (metaConn) {
        mapped.push({
          id: `meta:instagram:${metaConn.account_id || metaConn.tenant_id}`,
          platform: 'instagram',
          username: metaConn.account_name || '(via Meta)',
          provider: 'meta',
        });
      }
      setAccounts(mapped);
    } catch (error) {
      console.error('Fetch social accounts error:', error);
    }
  };

  const handleConnect = (platform) => {
    const oauthMap = { facebook: 'meta', instagram: 'meta', linkedin: 'linkedin_ads', twitter: null };
    const oauthProvider = oauthMap[platform.id];
    if (!oauthProvider) {
      toast.info(`${platform.name} OAuth is not yet wired. Coming soon.`);
      return;
    }
    startOAuthFlow(oauthProvider, platform.name);
  };

  const startOAuthFlow = async (oauthProvider, friendlyName) => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oauth/${oauthProvider}/authorize`);
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (err) {
      const detail = err.response?.data?.detail || `Could not start ${friendlyName} OAuth`;
      toast.error(detail);
      setConnecting(false);
    }
  };

  const handleConnectSubmit = async () => {
    if (!connectUsername.trim()) return;
    setConnecting(true);
    try {
      await axios.post(`${API_URL}/api/social/accounts/connect`, {
        platform: connectingPlatform.id,
        username: connectUsername.trim(),
      });
      toast.success(`${connectingPlatform.name} connected!`);
      setConnectDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (account) => {
    try {
      if (account.provider) {
        await axios.delete(`${API_URL}/api/oauth/${account.provider}`);
      } else {
        await axios.delete(`${API_URL}/api/social/accounts/${account.id}`);
      }
      toast.success('Account disconnected');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {socialPlatforms.map((platform) => {
          const account = accounts.find(a => a.platform === platform.id);
          const connected = !!account;
          const Icon = platform.icon;
          return (
            <div key={platform.id} className="flex items-center justify-between p-4 border border-gray-border rounded-lg" data-testid={`settings-social-${platform.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${platform.color}15` }}>
                  <Icon className="h-5 w-5" style={{ color: platform.color }} />
                </div>
                <div>
                  <p className="font-medium text-text-primary">{platform.name}</p>
                  {connected && <p className="text-xs text-gray-medium">@{account.username}</p>}
                </div>
              </div>
              <Button
                variant={connected ? 'outline' : 'default'}
                size="sm"
                className={!connected ? 'bg-emerald-brand hover:bg-emerald-hover' : ''}
                onClick={() => connected ? handleDisconnect(account) : handleConnect(platform)}
              >
                {connected ? <><Unplug className="h-3.5 w-3.5 mr-1" />Disconnect</> : <><Link2 className="h-3.5 w-3.5 mr-1" />Connect</>}
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {connectingPlatform && (
                <>
                  <connectingPlatform.icon className="h-5 w-5" style={{ color: connectingPlatform.color }} />
                  Connect {connectingPlatform.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Enter your {connectingPlatform?.name} username to connect your account.
              </p>
            </div>
            <div>
              <Label htmlFor="settings-oauth-username">Username / Handle</Label>
              <Input
                id="settings-oauth-username"
                placeholder={`@your${connectingPlatform?.id || ''}handle`}
                value={connectUsername}
                onChange={(e) => setConnectUsername(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4 justify-end">
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-emerald-brand hover:bg-emerald-hover"
              onClick={handleConnectSubmit}
              disabled={connecting || !connectUsername.trim()}
            >
              {connecting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Authorize & Connect
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
